import React from 'react';
import { Sparkles } from 'lucide-react';
import type { Badge } from '../types/badges';

interface BadgeListProps {
  badges: Badge[];
  earnedBadges: string[];
  totalPlaytimeHours: number;
  totalAchievements: number;
}

export function BadgeList({ badges, earnedBadges, totalPlaytimeHours, totalAchievements }: BadgeListProps) {
  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">XP</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {badges.map((badge) => {
              const isEarned = earnedBadges.includes(badge.id);
              const Icon = badge.icon;
              const progress = Math.min(100, (
                badge.category === 'playtime'
                  ? (totalPlaytimeHours / badge.requirement) * 100
                  : (totalAchievements / badge.requirement) * 100
              ));
              
              return (
                <tr
                  key={badge.id}
                  className={`${isEarned ? 'bg-indigo-50' : 'hover:bg-gray-50'} transition-colors`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        isEarned ? 'bg-indigo-100' : 'bg-gray-100'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          isEarned ? 'text-indigo-600' : 'text-gray-500'
                        }`} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${
                          isEarned ? 'text-indigo-900' : 'text-gray-900'
                        }`}>
                          {badge.name}
                        </span>
                        {isEarned && (
                          <Sparkles className="w-4 h-4 text-yellow-500" />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{badge.description}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      badge.category === 'playtime'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {badge.category === 'playtime' ? 'Playtime' : 'Achievements'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-48">
                      <div className="flex justify-between text-xs mb-1">
                        <span className={isEarned ? 'text-indigo-600' : 'text-gray-600'}>
                          {isEarned ? 'Completed!' : `${progress.toFixed(1)}%`}
                        </span>
                        <span className="text-gray-500">
                          {badge.category === 'playtime'
                            ? `${totalPlaytimeHours.toLocaleString()} / ${badge.requirement.toLocaleString()}h`
                            : `${totalAchievements.toLocaleString()} / ${badge.requirement.toLocaleString()}`
                          }
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isEarned ? 'bg-indigo-600' : 'bg-indigo-400'
                          }`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`font-medium ${
                      isEarned ? 'text-indigo-600' : 'text-gray-900'
                    }`}>
                      {badge.experiencePoints.toLocaleString()} XP
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}